import { z } from "zod"
import type { GroupableTurnArtifacts, TurnArtifactType } from "@common/components/chat/groupAssistantSteps"

export const REPLACE_MODE_TOOLS = new Set<string>(["live_review"]);

export type MessageType = "text" | "thinking" | "tool_call" | "tool_result"

export interface Citation {
  id: number | string
  number?: number
  document_title?: string
  cited_text?: string
  bookmarks?: string[]
  bookmark_start?: string
  bookmark_end?: string
  messageIndex?: number
}

export interface ChatMessage {
  content: string
  turnType: "user" | "assistant"
  turnArtifacts?: GroupableTurnArtifacts
  conversationId?: string
  // `tool_group` is a synthetic type produced by the ChatPage grouper
  // (see common/components/chat/groupAssistantSteps.ts) so the renderer
  // can swap in a <ToolCallGroup /> card for a chain of tool calls.
  messageType?: "tool_call" | "tool_result" | "tool_group"
  toolName?: string
  id?: string
  timestamp?: string

  propelAuthUserID?: string
  createdAt?: string
  // Terminal-state timestamp from the persisted row. NULL while streaming,
  // non-NULL once the BE has stamped completion (matching tool_result arrival,
  // terminal tool_status, end_turn, or interrupt). See
  // agent/turn_persistence.py:close_open_streams.
  completedAt?: string
  propelAuthOrgID?: string

  citations?: Citation[]
  bookmarks?: string[]

  isTransient?: boolean
}

export { COLLAPSIBLE_TOOLS } from "@common/constants/tools"

export function getMessageType(message: ChatMessage): TurnArtifactType {
  return message.messageType || message.turnArtifacts?.type || "text"
}

export const actionFailureSchema = z.object({
  reason: z.string(),
  isPartiallyApplied: z.boolean(),
  conflictingActionIds: z.array(z.string()).default([]),
  writtenBack: z.boolean().optional(),
})

export type ActionFailure = z.infer<typeof actionFailureSchema>

const APPLICABLE_ACTION_TOOL_NAMES = [
  "redline_section",
  "redline_selection",
  "edit_table",
  "change_throughout",
  "add_paragraph",
  "add_comment",
  "reply_to_comment",
] as const

type ApplicableActionToolName = (typeof APPLICABLE_ACTION_TOOL_NAMES)[number]

export type BatchApplicableActionMessage = ChatMessage & {
  toolName: Exclude<ApplicableActionToolName, "redline_selection">
}

export function isToolErrorResult(content: string): boolean {
  const trimmed = content.trim()
  if (!trimmed.startsWith("{") || !trimmed.includes('"error"')) return false
  try {
    const parsed: unknown = JSON.parse(trimmed)
    return typeof parsed === "object" && parsed !== null && (parsed as { error?: unknown }).error === true
  } catch {
    return false
  }
}

export function isApplicableActionMessage(message: ChatMessage): message is ChatMessage & { toolName: ApplicableActionToolName } {
  return (
    message.turnType === "assistant" &&
    getMessageType(message) === "tool_result" &&
    APPLICABLE_ACTION_TOOL_NAMES.some((applicable) => applicable === message.toolName) &&
    !isToolErrorResult(message.content)
  )
}

export function isBatchApplicableActionMessage(message: ChatMessage): message is BatchApplicableActionMessage {
  return isApplicableActionMessage(message) && message.toolName !== "redline_selection"
}

export function getActionIdentifier(message: ChatMessage, fallbackId: string): string {
  const toolCallId = message.turnArtifacts?.tool_call_id?.trim()
  if (toolCallId) return toolCallId
  const id = message.id?.trim()
  if (id) return id
  return fallbackId
}

export function liveReviewActionScopeOf(message: ChatMessage): string {
  return message.turnArtifacts?.source_tool_call_id ?? message.id
}

export function isThinking(message: ChatMessage): boolean {
  return message.content === "Thinking..."
}
